// [Phase 3(c) Sub-stage 2] Reusable Ley 40 applicability-gate questionnaire.
//
// ONE component, TWO entry points (spec: reuse, don't build twice):
//   - the post-auth registration gate step (Register.jsx), and
//   - the editable "Ley 40 applicability" section on /settings.
//
// Honesty rules honored:
//   * Questions are DB-served (GET /users/me/ley40-gate → db.law_meta), rendered
//     AS SERVED — never paraphrased or hardcoded (spec §2.1/§5).
//   * Three-state answers: Yes (true) / No (false) / Not yet answered (null). The
//     third is a REAL state, never defaulted to No (spec §0 rule 2). Existing
//     users arrive pre-populated from the legacy mapping (02/03) with 01/04/05
//     null — handled server-side; this component just renders what it is served.
//   * verbatim_es (enacted Spanish) is surfaceable on demand so a bilingual user
//     can check each question against the statute.
import React, { useEffect, useState } from 'react';
import { ley40GateAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/i18n/TranslationContext';
import { cn } from '@/lib/utils';

export default function Ley40GateQuestionnaire({ onSaved }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});     // condition_id -> true | false | null
  const [evaluation, setEvaluation] = useState(null);
  const [openEs, setOpenEs] = useState({});       // condition_id -> show verbatim_es
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await ley40GateAPI.get();
      const qs = res.data.questions || [];
      setQuestions(qs);
      const a = {};
      qs.forEach(q => { a[q.condition_id] = q.answer ?? null; });
      setAnswers(a);
      setEvaluation(res.data.evaluation || null);
    } catch (e) {
      setError(e.response?.data?.detail || t('gate_load_error'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setAnswer = (cid, val) => setAnswers(a => ({ ...a, [cid]: val }));

  const handleSave = async () => {
    setSaving(true);
    // Only send ANSWERED (true/false) triggers. The PUT endpoint merges and does
    // not support un-answering (built merge-only in Stage 3), so "not yet answered"
    // triggers are simply not sent — never coerced to No. [A31]
    const payload = {};
    Object.entries(answers).forEach(([cid, v]) => { if (v === true || v === false) payload[cid] = v; });
    try {
      const res = await ley40GateAPI.update(payload);
      setEvaluation(res.data.evaluation || null);
      toast.success(t('gate_saved'));
      if (onSaved) onSaved(res.data.evaluation);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('gate_save_error'));
    } finally { setSaving(false); }
  };

  // Plain-language derived applicability, from the last SAVED evaluation.
  const derivedLine = () => {
    if (!evaluation) return null;
    if (evaluation.provisional && !evaluation.in_scope) {
      const n = (evaluation.unanswered_trigger_ids || []).length;
      return t('gate_result_provisional').replace('{n}', String(n));
    }
    return evaluation.in_scope ? t('gate_result_applies') : t('gate_result_not_apply');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" />{t('loading')}
      </div>
    );
  }
  if (error) return <div className="text-sm text-vrs-red py-4" data-testid="gate-error">{error}</div>;

  return (
    <div className="space-y-4" data-testid="ley40-gate-questionnaire">
      <p className="text-xs text-muted-foreground">{t('gate_intro')}</p>
      {questions.map(q => (
        <div key={q.condition_id} className="rounded-lg border border-border/40 px-4 py-3 space-y-2" data-testid="gate-question">
          <div className="flex items-start gap-2">
            <span className="text-xs font-mono text-muted-foreground mt-0.5">{q.order}.</span>
            {/* Rendered AS SERVED — never paraphrased (spec §5). */}
            <p className="text-sm flex-1">{q.question_en}</p>
          </div>
          <div className="flex flex-wrap gap-2 pl-5">
            <Button type="button" size="sm" variant={answers[q.condition_id] === true ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setAnswer(q.condition_id, true)} data-testid="gate-yes">{t('yes')}</Button>
            <Button type="button" size="sm" variant={answers[q.condition_id] === false ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setAnswer(q.condition_id, false)} data-testid="gate-no">{t('no')}</Button>
            <Button type="button" size="sm" variant={answers[q.condition_id] == null ? 'secondary' : 'ghost'} className="h-7 text-xs" onClick={() => setAnswer(q.condition_id, null)} data-testid="gate-unanswered">{t('gate_not_yet_answered')}</Button>
          </div>
          {q.verbatim_es && (
            <Collapsible open={!!openEs[q.condition_id]} onOpenChange={o => setOpenEs(s => ({ ...s, [q.condition_id]: o }))}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground pl-5">
                <ChevronDown className={cn('h-3 w-3 transition-transform', openEs[q.condition_id] && 'rotate-180')} />{t('gate_view_statute')}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <p className="text-[10px] text-muted-foreground italic leading-relaxed pl-5 pt-1" data-testid="gate-verbatim-es">{q.verbatim_es}</p>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-xs text-muted-foreground" data-testid="gate-derived">{derivedLine()}</p>
        <Button onClick={handleSave} disabled={saving} data-testid="gate-save">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('saving')}</> : t('save')}
        </Button>
      </div>
    </div>
  );
}
